import { map } from "lodash";
import { Select } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";

import SelectComponents from "src/components/SelectComponents";
import usePagedTagSearch from "src/hooks/usePagedTagSearch";

import { SelectorContext } from "../TagsEditor/contexts/selectorContext";
import { MenuList } from "./components/MenuList";
import useSelectedTagValue from "./hooks/useSelectedTagValue";

const DEFAULT_PLACEHOLDER = "Type to search tag name or type";

export const toTagSearchOptions = (tags, pickableTagTypes) => {
  const pickableSet =
    pickableTagTypes == null ? null : new Set(pickableTagTypes);
  return map(tags, (tag) => ({
    label: tag.displayValue,
    value: tag.id,
    isDisabled: pickableSet != null && !pickableSet.has(tag.type),
  }));
};

const TagSearchSelect = ({
  tagTypes,
  pickableTagTypes,
  value,
  onSelectTag,
  label,
  ariaLabel,
  disabled,
  invalid,
  mandatory,
  focus,
  placeholder = DEFAULT_PLACEHOLDER,
  menuPortalTarget,
  menuPosition,
  updateStyles: updateStylesProp,
}) => {
  const { selectedValue, setSelectedValue, selectedValueLoading } =
    useSelectedTagValue(value);

  // Add state to control the input value
  const [inputValue, setInputValue] = useState("");

  const skipEmptyAllowlist = Array.isArray(tagTypes) && tagTypes.length === 0;
  const { tags, loading, loadOptions, loadMore, loadMoreLoading } =
    usePagedTagSearch({
      tagTypes,
      // [] is "all types" on the API — skip so an empty allowlist stays empty.
      skip: skipEmptyAllowlist,
    });

  const handleSelect = (selected) => {
    if (selected?.isDisabled) {
      return;
    }
    setSelectedValue(selected);
    onSelectTag(selected);
    // Clear the input value when an option is selected
    setInputValue("");
  };

  const options = toTagSearchOptions(tags, pickableTagTypes);

  const handleInputChange = useCallback(
    (val, action) => {
      // Always update the input value
      setInputValue(val);

      // Only load options on actual input changes, not on blur or menu close
      if (
        !skipEmptyAllowlist &&
        action.action !== "input-blur" &&
        action.action !== "menu-close"
      ) {
        loadOptions(val);
      }
    },
    [loadOptions, skipEmptyAllowlist]
  );

  // Load all options when the menu opens
  const handleMenuOpen = useCallback(() => {
    // Clear any stale input value when menu opens
    setInputValue("");
    if (skipEmptyAllowlist) {
      return;
    }
    loadOptions("");
  }, [loadOptions, skipEmptyAllowlist]);

  const updateStyles = (styles) => {
    const withPortal = menuPortalTarget
      ? {
          ...styles,
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }
      : styles;
    return updateStylesProp ? updateStylesProp(withPortal) : withPortal;
  };

  return (
    <SelectorContext.Provider
      value={{
        loadMoreLoading,
      }}
    >
      <Select
        inputId="tagSearchSelector"
        label={label}
        ariaLabel={ariaLabel || label || "Search tags"}
        isDisabled={disabled}
        invalid={invalid}
        placeholder={placeholder}
        noOptionsMessage={() => "No search results"}
        cacheOptions={false}
        backspaceRemovesValue={true}
        isClearable={true}
        isSearchable
        isLoading={loading || selectedValueLoading}
        options={loading && options.length === 0 ? [] : options}
        onChange={handleSelect}
        onInputChange={handleInputChange}
        onMenuOpen={handleMenuOpen}
        isOptionDisabled={(option) => Boolean(option?.isDisabled)}
        value={selectedValue}
        inputValue={inputValue}
        menuPlacement="auto"
        menuPortalTarget={menuPortalTarget || undefined}
        menuPosition={menuPosition || (menuPortalTarget ? "fixed" : undefined)}
        menuShouldScrollIntoView={!menuPortalTarget}
        closeMenuOnScroll={Boolean(menuPortalTarget)}
        updateStyles={updateStyles}
        components={{
          ...SelectComponents,
          MenuList,
        }}
        isValidNewOption={() => {
          return false;
        }}
        selectType="creatable"
        focus={focus}
        mandatory={mandatory}
        onMenuScrollToBottom={loadMore}
      />
    </SelectorContext.Provider>
  );
};

TagSearchSelect.propTypes = {
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  value: PropTypes.string,
  onSelectTag: PropTypes.func,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  mandatory: PropTypes.bool,
  focus: PropTypes.bool,
  tagTypes: PropTypes.array,
  pickableTagTypes: PropTypes.array,
  placeholder: PropTypes.string,
  menuPortalTarget: PropTypes.object,
  menuPosition: PropTypes.string,
  updateStyles: PropTypes.func,
};

export default TagSearchSelect;
